import { BUSINESS_UNIT_LABELS } from '@/constants/businessUnits';
import React, { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Search, TrendingUp, DollarSign, CalendarIcon, X, Trash2 } from 'lucide-react';
import { formatCurrency } from '@/lib/currency';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { useCashInflows, useDeleteCashInflow, INFLOW_CATEGORY_LABELS, InflowCategory } from '@/hooks/finance/useCashInflows';
import { useBankAccounts } from '@/hooks/use-bank-accounts';
import { format, parseISO } from 'date-fns';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { DeleteConfirmationDialog } from '@/components/finance/DeleteConfirmationDialog';
import { useFinancePagination } from '@/hooks/use-finance-pagination';
import FinancePagination from '@/components/finance/FinancePagination';
import { cn } from '@/lib/utils';

const CashInflowsPage = () => {
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [inflowToDelete, setInflowToDelete] = React.useState<{ id: string; number: string } | null>(null);

  const isAdmin = userRole === 'admin';

  const searchTerm = searchParams.get('search') || '';
  const selectedCategory = searchParams.get('cat') || 'all';
  const selectedBankAccount = searchParams.get('bank') || 'all';
  const dateFromStr = searchParams.get('from') || '';
  const dateToStr = searchParams.get('to') || '';

  const dateFrom = dateFromStr ? new Date(dateFromStr) : undefined;
  const dateTo = dateToStr ? new Date(dateToStr) : undefined;

  const updateParam = (key: string, value: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (!value || value === 'all' || value === '') {
        next.delete(key);
      } else {
        next.set(key, value);
      }
      next.delete('page');
      return next;
    });
  };

  const { data: inflows, isLoading } = useCashInflows();
  const { data: bankAccounts } = useBankAccounts(true);
  const deleteInflow = useDeleteCashInflow();

  const filteredInflows = useMemo(() => {
    if (!inflows) return [];
    return inflows.filter(inflow => {
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const matchesText = 
          inflow.inflow_number.toLowerCase().includes(term) ||
          (inflow.description || '').toLowerCase().includes(term) ||
          (inflow.source_name || '').toLowerCase().includes(term) ||
          (inflow.reference_number || '').toLowerCase().includes(term) ||
          inflow.amount.toString().includes(term);
        if (!matchesText) return false;
      }
      if (selectedCategory !== 'all' && inflow.category !== selectedCategory) return false;
      if (selectedBankAccount !== 'all' && inflow.bank_account_id !== selectedBankAccount) return false;
      if (dateFrom && new Date(inflow.inflow_date) < dateFrom) return false;
      if (dateTo && new Date(inflow.inflow_date) > dateTo) return false;
      return true;
    });
  }, [inflows, searchTerm, selectedCategory, selectedBankAccount, dateFrom, dateTo]);

  const totalAmount = useMemo(() => filteredInflows.reduce((sum, i) => sum + i.amount, 0), [filteredInflows]);

  const pagination = useFinancePagination({ data: filteredInflows, itemsPerPage: 20 });

  const handleDelete = (id: string, number: string) => {
    setInflowToDelete({ id, number });
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (inflowToDelete) {
      deleteInflow.mutate(inflowToDelete.id);
      setDeleteDialogOpen(false);
      setInflowToDelete(null);
    }
  };

  const hasActiveFilters = searchTerm || selectedCategory !== 'all' || selectedBankAccount !== 'all' || dateFromStr || dateToStr;

  const clearFilters = () => {
    setSearchParams({});
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Cash Inflows</h1>
            <p className="text-muted-foreground">Record and track capital injections, loans, and other financing inflows</p>
          </div>
          <Button onClick={() => navigate('/finance/accounting/cash-inflows/new')}>
            <Plus className="h-4 w-4 mr-2" />
            New Cash Inflow
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-emerald-500" />
                <div>
                  <p className="text-sm text-muted-foreground">Total Inflows</p>
                  <p className="text-2xl font-bold">{formatCurrency(totalAmount)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-sm text-muted-foreground">Records</p>
                  <p className="text-2xl font-bold">{filteredInflows.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by number, source, amount..."
                    value={searchTerm}
                    onChange={(e) => updateParam('search', e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <Select value={selectedCategory} onValueChange={(v) => updateParam('cat', v)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {(Object.entries(INFLOW_CATEGORY_LABELS) as [InflowCategory, string][]).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedBankAccount} onValueChange={(v) => updateParam('bank', v)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Bank Account" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Accounts</SelectItem>
                  {bankAccounts?.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-[140px] justify-start text-left font-normal", !dateFrom && "text-muted-foreground")}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateFrom ? format(dateFrom, 'MMM d, yyyy') : 'From'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={dateFrom} onSelect={(d) => updateParam('from', d ? format(d, 'yyyy-MM-dd') : '')} /></PopoverContent>
              </Popover>

              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-[140px] justify-start text-left font-normal", !dateTo && "text-muted-foreground")}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateTo ? format(dateTo, 'MMM d, yyyy') : 'To'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={dateTo} onSelect={(d) => updateParam('to', d ? format(d, 'yyyy-MM-dd') : '')} /></PopoverContent>
              </Popover>

              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-1" /> Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* List */}
        {isLoading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => <Skeleton key={i} className="h-20 w-full" />)}
          </div>
        ) : filteredInflows.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <TrendingUp className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">No cash inflows found</h3>
              <p className="text-muted-foreground mt-1">Record your first capital injection or financing inflow</p>
              <Button className="mt-4" onClick={() => navigate('/finance/accounting/cash-inflows/new')}>
                <Plus className="h-4 w-4 mr-2" /> Record Cash Inflow
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {pagination.paginatedData.map((inflow) => (
              <Card
                key={inflow.id}
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => navigate(`/finance/accounting/cash-inflows/${inflow.id}`)}
              >
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{inflow.inflow_number}</span>
                          <Badge variant="secondary">{INFLOW_CATEGORY_LABELS[inflow.category]}</Badge>
                          {(inflow as any).business_unit && (
                            <Badge variant="outline">{BUSINESS_UNIT_LABELS[(inflow as any).business_unit as keyof typeof BUSINESS_UNIT_LABELS] || (inflow as any).business_unit}</Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">
                          {format(parseISO(inflow.inflow_date), 'MMM d, yyyy')}
                          {inflow.source_name && ` • ${inflow.source_name}`}
                          {inflow.bank_account?.name && ` • ${inflow.bank_account.name}`}
                        </div>
                        {inflow.description && (
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-1">{inflow.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-semibold text-emerald-600">
                        +{formatCurrency(inflow.amount, inflow.currency)}
                      </span>
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={(e) => { e.stopPropagation(); handleDelete(inflow.id, inflow.inflow_number); }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            <FinancePagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={pagination.goToPage}
              hasNextPage={pagination.hasNextPage}
              hasPreviousPage={pagination.hasPreviousPage}
              totalItems={filteredInflows.length}
              startIndex={pagination.startIndex}
              endIndex={pagination.endIndex}
              itemsPerPage={pagination.itemsPerPage}
              onItemsPerPageChange={pagination.changePageSize}
            />
          </div>
        )}
      </div>

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={confirmDelete}
        isDeleting={deleteInflow.isPending}
        title="Delete Cash Inflow"
        description={`Are you sure you want to delete inflow ${inflowToDelete?.number}? This action cannot be undone.`}
      />
    </Layout>
  );
};

export default CashInflowsPage;
